
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Bell } from 'lucide-react';
import JsonLd from '../components/SEO/JsonLd';
import Header from '../components/layout/Header';
import ImprovedUniversalHeader from '../components/ImprovedUniversalHeader';
import LandlordHeader from '../components/LandlordHeader';
import { useAuth } from '../contexts/AuthContext';
import { useRealTimeNotifications } from '../hooks/useRealTimeNotifications';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import MobileNotificationCenter from '../components/MobileNotificationCenter';

const ContactUs = () => {
  const navigate = useNavigate();
  const { user, logout, profile } = useAuth();
  const { stats } = useRealTimeNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Contact form submitted:', formData);
    // You would typically send this to your backend
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Determine which header to render based on authentication state - exactly like the dashboard pages
  const renderHeader = () => {
    if (!user) {
      return <Header />;
    }
    
    // Check if user is landlord - same logic as LandlordDashboard.tsx
    if (profile?.user_type === 'landlord') {
      return <LandlordHeader user={user} onSignOut={handleSignOut} />;
    }
    
    // For renters - same setup as Dashboard.tsx
    const rightActions = (
      <Popover open={showNotifications} onOpenChange={setShowNotifications}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="relative p-2 hover:bg-slate-800/50 rounded-xl transition-colors text-white"
          >
            <Bell className="w-4 h-4" />
            {stats.unread > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {stats.unread > 99 ? '99+' : stats.unread}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          align="end" 
          className="w-80 sm:w-96 p-0 bg-background border shadow-lg"
          sideOffset={8}
        >
          <MobileNotificationCenter className="border-0 shadow-none" />
        </PopoverContent>
      </Popover>
    );

    return (
      <ImprovedUniversalHeader 
        title="Contact Us" 
        showBackButton={false} 
        rightActions={rightActions}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {renderHeader()}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Page Title Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Get in touch with our team. We're here to help with any questions or concerns.
          </p>
        </div>

        {/* JSON-LD: ContactPage & Organization */}
        <JsonLd data={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Flamingo',
          url: `${window.location.origin}/contact`,
          mainEntity: {
            '@type': 'Organization',
            name: 'Flamingo',
            url: window.location.origin,
            contactPoint: [{
              '@type': 'ContactPoint',
              contactType: 'customer support',
              email: 'support@flamingolisting.com'
            }]
          }
        }} />
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-white mb-6">
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-200 mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-pink-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-pink-500"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-200 mb-2">
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-pink-500"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-200 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help..."
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-pink-500"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-pink-500 hover:bg-pink-600 text-white"
              >
                Send Message
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="p-6 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <Mail className="w-6 h-6 text-pink-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">Email Us</h3>
              </div>
              <p className="text-slate-300 mb-2">For general inquiries:</p>
              <p className="text-pink-400 font-medium">info@flamingolisting.com</p>
              <p className="text-slate-300 mt-3 mb-2">For support:</p>
              <p className="text-pink-400 font-medium">support@flamingolisting.com</p>
            </Card>

            <Card className="p-6 bg-slate-800/50 border-slate-700 backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <Clock className="w-6 h-6 text-pink-400 mr-3" />
                <h3 className="text-lg font-semibold text-white">Business Hours</h3>
              </div>
              <div className="text-slate-300 space-y-1">
                <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                <p>Saturday: 10:00 AM - 4:00 PM EST</p>
                <p>Sunday: Closed</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
